// Завершення сеансу після бездіяльності (ТЗ 4.1.5.3, TZ-DELTAS п.11): тривалість — налаштування сервера
// (SESSION_IDLE_TIMEOUT_MIN, GET /api/admin/policy; недоступне — типове DEFAULT_SESSION_IDLE_TIMEOUT_MIN). Активність — дії
// користувача (миша, клавіатура, дотик, прокрутка), спільна для вкладок через localStorage; фонове опитування сповіщень
// активністю не вважається. За хвилину до виходу — попередження з «Продовжити».
// Повторне відкриття вкладки чи браузера не скидає лічильник: якщо остання дія старша за тайм-аут і сталася після входу
// (сеанс відновлено refresh-токеном, а не новим входом), вихід — одразу. Серверна частина — запит R3 U16-to-F.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { toast } from '@/lib/toast';
import { Button, Dialog } from '@/ui';

const STORAGE_KEY = 'unisched.lastActivity';
const EVENTS = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'wheel', 'scroll'] as const;
/** Як часто записувати активність у сховище (мс) — не на кожен рух миші. */
const WRITE_EVERY_MS = 10_000;
const CHECK_EVERY_MS = 5_000;
const WARN_BEFORE_MS = 60_000;

function readShared(): number {
  try {
    const v = Number(window.localStorage.getItem(STORAGE_KEY));
    return Number.isFinite(v) ? v : 0;
  } catch {
    return 0;
  }
}

function writeShared(t: number): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(t));
  } catch {
    // сховище недоступне — облік лише в цій вкладці
  }
}

/**
 * Чи завершився сеанс ще до відкриття сторінки: остання дія (у будь-якій вкладці) старша за тайм-аут і не раніша за вхід —
 * тобто сеанс відновлено refresh-токеном після бездіяльності. Щойно виконаний вхід (після останньої дії) — не завершений.
 */
function idleExpiredOnOpen(lastActivity: number, lastLoginAt: string | null, now: number, timeoutMs: number): boolean {
  if (!lastActivity || timeoutMs <= 0 || now - lastActivity < timeoutMs) return false;
  const login = lastLoginAt ? Date.parse(lastLoginAt) : 0;
  return !(Number.isFinite(login) && login > lastActivity);
}

/** timeoutMin: null — налаштування ще завантажуються (контроль не стартує), 0 — вимкнено сервером. */
export function SessionIdleGuard({ timeoutMin }: { timeoutMin: number | null }) {
  const { status, user, logout } = useAuth();
  const last = useRef(Date.now());
  const lastWrite = useRef(0);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const enabled = timeoutMin !== null && timeoutMin > 0 && status === 'authenticated';
  const lastLoginAt = user?.lastLoginAt ?? null;

  const touch = useCallback(() => {
    const now = Date.now();
    last.current = now;
    if (now - lastWrite.current > WRITE_EVERY_MS) {
      lastWrite.current = now;
      writeShared(now);
    }
  }, []);

  useEffect(() => {
    if (!enabled || timeoutMin === null) return;
    const timeoutMs = timeoutMin * 60_000;
    const expire = () => {
      setSecondsLeft(null);
      toast.warning({ title: 'Сеанс завершено через бездіяльність', description: 'Увійдіть знову, щоб продовжити роботу.', duration: 0 });
      void logout();
    };
    const now = Date.now();
    if (idleExpiredOnOpen(readShared(), lastLoginAt, now, timeoutMs)) {
      expire();
      return;
    }
    last.current = now;
    lastWrite.current = now;
    writeShared(now);
    const onActivity = () => touch();
    for (const e of EVENTS) window.addEventListener(e, onActivity, { passive: true });
    const timer = window.setInterval(() => {
      const idle = Date.now() - Math.max(last.current, readShared());
      if (idle >= timeoutMs) {
        window.clearInterval(timer);
        expire();
      } else if (idle >= timeoutMs - WARN_BEFORE_MS) {
        setSecondsLeft(Math.ceil((timeoutMs - idle) / 1000));
      } else {
        setSecondsLeft(null);
      }
    }, CHECK_EVERY_MS);
    return () => {
      window.clearInterval(timer);
      for (const e of EVENTS) window.removeEventListener(e, onActivity);
    };
  }, [enabled, timeoutMin, lastLoginAt, logout, touch]);

  if (!enabled || secondsLeft === null) return null;
  const keep = () => {
    lastWrite.current = 0;
    touch();
    setSecondsLeft(null);
  };
  return (
    <Dialog
      open
      onClose={keep}
      size="sm"
      title="Сеанс скоро завершиться"
      description={`Ви не працювали ${timeoutMin} хв. Сеанс завершиться приблизно за ${secondsLeft} с.`}
      footer={
        <>
          <Button variant="secondary" onClick={() => void logout()}>
            Вийти
          </Button>
          <Button variant="primary" onClick={keep} data-autofocus>
            Продовжити роботу
          </Button>
        </>
      }
    />
  );
}
